'use client';

import React from 'react';
import Link from 'next/link';
import AddOnCategoryBadges from 'components/AddOns/AddOnCategoryBadges/AddOnCategoryBadges';
import { highlightText, highlightHtml } from 'components/AddOns/SearchHighlight/SearchHighlight';
import { useTekstClient } from 'hooks/useTekstClient';
import styles from './AddOnCard.module.scss';

/**
 * Returns the provider name for an add-on.
 * Primary source: `provider` resolved by the backend from the Aanbieders category.
 * Fallback: extract from title parentheses e.g. "Add-on (Provider Name)".
 */
function getProviderName(addOn) {
    if (typeof addOn?.provider === 'string' && addOn.provider.trim()) {
        return addOn.provider.trim();
    }

    const title = addOn?.content_gepubliceerd?.titel;
    if (typeof title === 'string') {
        const match = title.match(/\(([^)]+)\)\s*$/);
        if (match?.[1]?.trim()) return match[1].trim();
    }
    return null;
}

/**
 * Returns the product status name from the status filter category.
 * Used to determine the status dot color (green = active, grey = "In ontwikkeling").
 * Fallback: if no status match, will return In ontwikkeling.
 */
function getProductStatus(addOn, filters, fallbackStatus) {
    const statusFilter = filters?.find((f) => f.key === 'status');
    if (statusFilter?.subfilters?.length && Array.isArray(addOn?.categorieen)) {
        const subfilterIds = new Set(statusFilter.subfilters.map((s) => s.id));
        const subfilterNames = new Map(statusFilter.subfilters.map((s) => [s.id, s.naam]));
        const match = addOn.categorieen.find((c) => c?.id && subfilterIds.has(c.id));
        if (match) return subfilterNames.get(match.id) ?? fallbackStatus;
    }
    return fallbackStatus;
}

/**
 * Add-on card for list view. Layout: 
 * circular logo (left)
 * title + provider (center)
 * description (right)
 * badges (bottom) 
 */
export default function AddOnCard({ addOn, filters = [], search = null, detailUrl }) {
    const { tekst } = useTekstClient();
    if (!addOn?.content_gepubliceerd) return null;

    const providerName = getProviderName(addOn);
    const productStatus = getProductStatus(addOn, filters, tekst('mkgeu:mkgaddons:card:status:indevelopment'));
    const normalizedStatus = String(productStatus ?? '').toLowerCase();
    const normalizedOnline = String(tekst('mkgeu:mkgaddons:card:status:online') ?? '').toLowerCase();
    const isLive = normalizedStatus.includes('online') || normalizedStatus.includes('live') || normalizedStatus === normalizedOnline;
    const titel = addOn.content_gepubliceerd.titel;
    const intro = addOn.content_gepubliceerd.intro || addOn.content_gepubliceerd.bericht || '';
    const media = addOn.overzicht_wiki?.content_media?.media;
    const imgSrc = media?.id ? `${process.env.NEXT_PUBLIC_BACKEND}/api/media/${media.id}`: null;

    return (
        <article className={styles.card}>
            <Link
                href={detailUrl}
                prefetch={false}
                className={styles.cardOverlayLink}
                aria-label={titel}
            />
            <span
                className={`${styles.statusDot} ${isLive ? styles.statusDotGreen : styles.statusDotGrey}`}
                title={productStatus}
            />
            <div className={styles.cardInner}>
                <div className={styles.leftSection}>
                    <div className={styles.imageSection}>
                        {imgSrc ? (
                            <div className={styles.imageWrapper}>
                                <img
                                    src={imgSrc}
                                    alt={media?.intro ?? titel}
                                    loading="lazy"
                                    className={styles.image}
                                />
                            </div>
                        ) : (
                            <div className={styles.imageWrapper} aria-hidden="true" />
                        )}
                    </div>
                    <div className={styles.titleProvider}>
                        <h2 className={styles.title}>
                            {search ? highlightText(titel, search) : titel}
                        </h2>
                        {providerName && (
                            <div className={styles.provider}>
                                <span className={styles.providerLabel}>{tekst('mkgeu:mkgaddons:card:providerlabel')}</span>
                                <span className={styles.providerName}>{providerName}</span>
                            </div>
                        )}
                    </div>
                </div>
                <div className={styles.rightSection}>
                    <div className={styles.description}>
                        {intro && (
                            <div className={styles.intro}>
                                {highlightHtml(intro, search)}
                            </div>
                        )}
                    </div>
                </div>
            </div>
            <div className={styles.badgesRow}>
                <AddOnCategoryBadges addOn={addOn} filters={filters} />
            </div>
        </article>
    );
}
